import React, { useMemo } from 'react';

/** Normalize any known date string to dd/mm/yyyy digits+slashes, or ''. */
export const toDdMmYyyy = (value) => {
  if (value == null) return '';
  const str = String(value).trim();
  if (!str) return '';

  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return `${iso[3]}/${iso[2]}/${iso[1]}`;
  }

  const european = str.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (european) {
    const dd = european[1].padStart(2, '0');
    const mm = european[2].padStart(2, '0');
    return `${dd}/${mm}/${european[3]}`;
  }

  return maskDdMmYyyy(str.replace(/\D/g, '').slice(0, 8));
};

/** Clamp digit string into a progressive dd/mm/yyyy mask. */
export const maskDdMmYyyy = (rawDigits) => {
  let digits = String(rawDigits || '').replace(/\D/g, '').slice(0, 8);
  if (!digits) return '';

  // Auto zero-pad when first day digit is 4-9
  if (digits.length >= 1 && Number(digits[0]) > 3) {
    digits = `0${digits}`.slice(0, 8);
  }
  // Auto zero-pad month when first month digit is 2-9
  if (digits.length >= 3 && Number(digits[2]) > 1) {
    digits = `${digits.slice(0, 2)}0${digits.slice(2)}`.slice(0, 8);
  }

  let day = digits.slice(0, 2);
  let month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  if (day.length === 2) {
    const d = Number(day);
    if (d === 0) day = '01';
    else if (d > 31) day = '31';
  }
  if (month.length === 2) {
    const m = Number(month);
    if (m === 0) month = '01';
    else if (m > 12) month = '12';
  }

  const digitCount = digits.length;
  if (digitCount <= 2) return day.slice(0, digitCount);
  if (digitCount <= 4) return `${day}/${month.slice(0, digitCount - 2)}`;
  return `${day}/${month}/${year}`;
};

/** True if value is a complete valid calendar date in dd/mm/yyyy. */
export const isValidDdMmYyyy = (value) => {
  const m = String(value || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return false;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || year < 1000) return false;
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

/**
 * Controlled text date input forced to dd/mm/yyyy regardless of browser locale.
 * Digits only; slashes inserted automatically. Invalid dates cleared on blur.
 */
const EuropeanDateInput = ({
  value,
  onChange,
  required = false,
  className = 'rounded-md shadow-sm border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50',
  id,
  name,
  disabled = false,
}) => {
  const displayValue = useMemo(() => toDdMmYyyy(value), [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    if (!raw) {
      onChange('');
      return;
    }
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    onChange(maskDdMmYyyy(digits));
  };

  const handleBlur = () => {
    if (!displayValue) return;
    const normalized = toDdMmYyyy(displayValue);
    if (normalized.length === 10 && isValidDdMmYyyy(normalized)) {
      if (normalized !== value) onChange(normalized);
      return;
    }
    onChange('');
  };

  const handleKeyDown = (e) => {
    if (
      e.ctrlKey ||
      e.metaKey ||
      e.altKey ||
      [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(e.key)
    ) {
      return;
    }
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    const normalized = toDdMmYyyy(pasted);
    onChange(normalized.length === 10 && !isValidDdMmYyyy(normalized) ? '' : normalized);
  };

  return (
    <input
      id={id}
      name={name}
      type="text"
      inputMode="numeric"
      autoComplete="bday"
      placeholder="jj/mm/aaaa"
      lang="fr-CH"
      spellCheck={false}
      required={required}
      disabled={disabled}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      maxLength={10}
      pattern="\d{2}/\d{2}/\d{4}"
      title="jj/mm/aaaa"
      className={className}
    />
  );
};

export default EuropeanDateInput;
